/**
 * PWA Install Prompt Utility
 *
 * - isPWA(): detect installed/standalone mode.
 * - Global beforeinstallprompt capture: initInstallPromptCapture() is called once
 *   at app startup (App.js) so the deferred install event is available app-wide
 *   WITHOUT a page reload. The conversion-flow install button (PendingProfileOverlay,
 *   Android branch) consumes it via useInstallPrompt() and fires it with promptInstall().
 *
 * Note: ProfileSignupSuccessScreen (the web-signup install screen) still captures the
 * event locally after its own reload trick — left as-is, deferred for restyle.
 */
import { useState, useEffect } from 'react';

// Detect if running as installed PWA (standalone mode)
export const isPWA = () =>
  window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;

// --- Global beforeinstallprompt capture ---------------------------------------

let deferredPrompt = null;
let captureInitialized = false;
const listeners = new Set();

const notify = () => listeners.forEach((cb) => cb(deferredPrompt));

// Register the global beforeinstallprompt listener. Call once at app startup.
export const initInstallPromptCapture = () => {
  if (captureInitialized) return;
  captureInitialized = true;

  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent Chrome's mini-infobar; we trigger prompt() from our own button.
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });

  // Clear once installed so the button can't fire a stale prompt.
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
};

// Fire the native install prompt. Returns 'accepted' | 'dismissed' | null (unavailable).
export const promptInstall = async () => {
  if (!deferredPrompt) return null;
  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  notify();
  return outcome;
};

// React binding for components: { canInstall, promptInstall }.
export const useInstallPrompt = () => {
  const [prompt, setPrompt] = useState(deferredPrompt);
  useEffect(() => {
    setPrompt(deferredPrompt); // sync in case the event fired before this mounted
    listeners.add(setPrompt);
    return () => {
      listeners.delete(setPrompt);
    };
  }, []);
  return { canInstall: !!prompt, promptInstall };
};
